<template>
    <div class="integrity-flow" :style="themeStyle">
        <div class="progress">
            <template v-for="i in 4">
                <div
                    v-if="i > 1"
                    :key="'line-' + i"
                    class="prog-line"
                    :class="{ completed: i - 1 < step }"
                ></div>
                <div
                    :key="'node-' + i"
                    class="prog-node"
                    :class="{ completed: i < step, active: i === step }"
                    @click="onProgressClick(i)"
                >{{ i < step ? '✓' : i }}</div>
            </template>
        </div>
        <div class="prog-label">Step {{ step }} of 4 · {{ stepNames[step - 1] }}</div>

        <!-- Step 1 -->
        <section v-show="step === 1" class="step">
            <div class="step-head">
                <span class="step-tag">Step 1 of 4</span>
                <h2>Integrity Principles</h2>
                <p class="lead">All {{ programName }} examinations are conducted remotely. Because candidates sit the examination in their own environment, trust is the foundation of every achievement we award. {{ programName }} places the highest value on candidate integrity — not only in the examination itself, but in every aspect of a candidate's involvement with our programs.</p>
            </div>

            <div class="block">
                <h3>Integrity Applies to Every Aspect of Your Participation</h3>
                <p>Integrity at {{ programName }} begins long before the examination and continues long after it. It covers, including but not limited to:</p>
                <ul class="plain-list">
                    <li>Registration and application</li>
                    <li>Purchase and payment</li>
                    <li>Learning and preparation</li>
                    <li>The examination itself</li>
                    <li>All communication and conduct with {{ programName }}, EAE, LearningFirst, and their platforms</li>
                </ul>
            </div>

            <div class="block">
                <h3>What Constitutes Dishonest Conduct</h3>
                <p>Dishonest conduct is any action that undermines the fairness and authenticity of {{ programName }} achievements — including but not limited to <strong>exam cheating, payment fraud, and any other violation</strong> of these integrity terms.</p>
            </div>

            <div class="section-hint">Confirm that you have read and understood</div>
            <div class="ack-list">
                <label
                    v-for="(text, index) in ackTexts[1]"
                    :key="'ack1-' + index"
                    class="ack"
                    :class="{ checked: checks[1][index] }"
                    @click.prevent="toggleCheck(1, index)"
                >
                    <span class="custom-box"></span>
                    <span class="ack-text">{{ text }}</span>
                </label>
            </div>
        </section>

        <!-- Step 2 -->
        <section v-show="step === 2" class="step">
            <div class="step-head">
                <span class="step-tag">Step 2 of 4</span>
                <h2>Examination Integrity</h2>
                <p class="lead">For remote examinations, the fairness and validity of every result rest on a clear division of responsibility: <strong>{{ programName }} defines clear rules and requirements; candidates are responsible for following them.</strong></p>
                <p class="lead lead-gap">To safeguard that fairness, {{ programName }} prevents, monitors, and identifies dishonest conduct across multiple dimensions, from registration to the end of the examination. Candidates are responsible for keeping their own conduct compliant, so that it is not flagged as anomalous by the system.</p>
            </div>

            <div class="block">
                <h3>A. Environment Requirements</h3>
                <ul class="plain-list">
                    <li>Sit the examination alone in a private, quiet room</li>
                    <li>Do not speak during the exam.</li>
                    <li>Remain visible on camera at all times</li>
                    <li>Maintain a stable internet connection for the full duration</li>
                </ul>
            </div>

            <div class="block">
                <h3>B. Procedure Requirements</h3>
                <ul class="plain-list">
                    <li>Complete the mandatory mock test before the examination</li>
                    <li>Submit answers manually before the timer expires</li>
                </ul>
            </div>

            <div class="block">
                <h3>C. Cheating Behavior</h3>
                <ol class="list-7">
                    <li>Use of a second electronic device — including but not limited to mobile phones, tablets, or additional computers — for any purpose during the examination</li>
                    <li>Use of any electronic or paper-based notes, reference materials, or external resources, unless explicitly permitted by the examination instructions</li>
                    <li>The individual taking the examination is not the registered candidate, or the candidate fails to provide valid identification during identity verification</li>
                    <li>Presence of any person other than the candidate in the examination room at any time during the examination</li>
                    <li>Use of chat applications, AI tools, screen sharing, remote control software, browser extensions, system plugins, or any other technical assistance to answer examination questions</li>
                    <li>Exploiting time zone differences to gain an unfair advantage, or assisting others in doing so</li>
                    <li>Deliberately leaking or disseminating examination questions, content, or materials</li>
                </ol>
                <div class="note">Even when conduct is not explicitly listed above, cheating may still be determined when multiple dimensions of evidence point to it.</div>
            </div>

            <div class="section-hint">Confirm that you acknowledge</div>
            <div class="ack-list">
                <label
                    v-for="(text, index) in ackTexts[2]"
                    :key="'ack2-' + index"
                    class="ack"
                    :class="{ checked: checks[2][index] }"
                    @click.prevent="toggleCheck(2, index)"
                >
                    <span class="custom-box"></span>
                    <span class="ack-text">{{ text }}</span>
                </label>
            </div>
        </section>

        <!-- Step 3 -->
        <section v-show="step === 3" class="step">
            <div class="step-head">
                <span class="step-tag">Step 3 of 4</span>
                <h2>Payment Integrity</h2>
                <p class="lead">{{ programName }} offers both free and paid content and services, delivered through various platforms, including {{ programName }}, EAE, and LearningFirst. Candidates have <strong>full freedom of choice</strong> in whether to pay. Every payment is governed by clear terms and conditions, and choosing to purchase means agreeing to those terms.</p>
                <p class="lead lead-gap">Because content and services are <strong>consumed upon delivery</strong>, using the rights and conveniences of payment platforms to obtain a refund after the content or service has been consumed, in whole or in part, is a serious act of fraud and is subject to sanctions.</p>
            </div>

            <div class="section-hint">Confirm that you acknowledge</div>
            <div class="ack-list">
                <label
                    v-for="(text, index) in ackTexts[3]"
                    :key="'ack3-' + index"
                    class="ack"
                    :class="{ checked: checks[3][index] }"
                    @click.prevent="toggleCheck(3, index)"
                >
                    <span class="custom-box"></span>
                    <span class="ack-text">{{ text }}</span>
                </label>
            </div>
        </section>

        <!-- Step 4 -->
        <section v-show="step === 4" class="step">
            <div class="step-head">
                <span class="step-tag">Step 4 of 4</span>
                <h2>Consequences &amp; Your Final Pledge</h2>
                <p class="lead">The integrity of every {{ programName }} achievement rests on the conduct of every candidate. A violation is not merely a breach of procedure — it undermines the fairness of every other candidate's achievement, and it carries consequences. The consequences below are set out in three tiers, according to the nature and seriousness of the violation.</p>
            </div>

            <div class="tiers">
                <div class="tier tier-1">
                    <div class="tier-badge">Tier 1</div>
                    <div class="tier-title">Environment &amp; Procedural Violations</div>
                    <div class="tier-desc">Automatic point deductions; accumulated penalties of 20 points or more render the score <strong>invalid</strong>.</div>
                </div>
                <div class="tier tier-2">
                    <div class="tier-badge">Tier 2</div>
                    <div class="tier-title">Exam Cheating</div>
                    <div class="tier-desc"><strong>Immediate cancellation</strong> of the examination score; {{ programName }} reserves the right to notify the school and to publish the candidate's name depending on the seriousness of the case.</div>
                </div>
                <div class="tier tier-3">
                    <div class="tier-badge">Tier 3</div>
                    <div class="tier-title">Payment Fraud</div>
                    <div class="tier-desc"><strong>Immediate cancellation of all examination scores</strong>; <strong>permanent ban</strong> from all {{ programName }} and EAE programs and LearningFirst. Depending on the seriousness of the case, notification to the candidate's school and teacher(s), and public listing of the candidate's name.</div>
                </div>
            </div>

            <p class="pledge-intro">Your final step is a pledge — your personal commitment to uphold the integrity of {{ programName }} and to accept the consequences described above.</p>

            <div class="pledge-box">
                <div class="pledge-label">"{{ pledgeTarget }}"</div>
                <div class="pledge-guide">Please type the pledge below, exactly as shown, to continue.</div>
            </div>
            <div class="pledge-input-wrap">
                <textarea
                    v-model="pledgeInput"
                    class="pledge-input"
                    :class="{ match: pledgeMatches, nomatch: pledgeInput.length > 0 && !pledgeMatches }"
                    placeholder="Type the pledge here…"
                    spellcheck="false"
                ></textarea>
                <div
                    class="pledge-status"
                    :class="{ ok: pledgeMatches, err: pledgeInput.length > 0 && !pledgeMatches }"
                >{{ pledgeStatusText }}</div>
            </div>
        </section>

        <div class="nav-btns">
            <button type="button" class="btn btn-ghost" @click="goBack">← Back</button>
            <button type="button" class="btn btn-primary" :disabled="!canContinue" @click="goNext">
                {{ step === 4 ? 'Continue to Confirmation →' : 'Continue →' }}
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'RegistrationIntegrityFlow',
    props: {
        programName: {
            type: String,
            default: 'IPsyO'
        },
        themeColor: {
            type: String,
            default: '#E00044'
        },
        themeSecondary: {
            type: String,
            default: '#B91C3C'
        },
        skipValidation: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            step: 1,
            maxVisited: 1,
            stepNames: [
                'Integrity Principles',
                'Examination Integrity',
                'Payment Integrity',
                'Consequences & Final Pledge'
            ],
            checks: {
                1: [false, false],
                2: [false, false, false, false, false],
                3: [false, false, false]
            },
            pledgeInput: ''
        }
    },
    computed: {
        themeStyle() {
            return {
                '--primary': this.themeColor,
                '--secondary': this.themeSecondary
            }
        },
        pledgeTarget() {
            return `I pledge to uphold the integrity of ${this.programName}, to comply with all the terms set out in this application, and to accept all consequences of any violation, including but not limited to the cancellation of my results.`
        },
        ackTexts() {
            const name = this.programName
            return {
                1: [
                    `I have read and understood the ${name} Integrity Principles. I understand that integrity applies not only to the examination itself, but to every aspect of my participation in ${name} programs — including but not limited to purchase, payment, study and preparation.`,
                    'I understand that dishonest conduct in any of these areas — including exam cheating and payment fraud — is a violation of the integrity terms and is subject to the consequences described in this application.'
                ],
                2: [
                    'I acknowledge that maintaining a compliant examination environment is my responsibility, and that non-compliant conditions will result in direct point deduction and, in serious cases, the invalidation of my score.',
                    'I acknowledge that completing the mandatory procedures is my responsibility, and that failure to complete them will result in direct point deduction.',
                    'I acknowledge that point deductions for environment and procedural violations are final, cannot be revoked, and are not subject to appeal.',
                    'I acknowledge that cheating is a serious violation of integrity, and that confirmed cheating will result in the immediate cancellation of my score.',
                    'I acknowledge that cheating may be determined when multiple dimensions of evidence point to it, even if my conduct is not explicitly listed in this policy.'
                ],
                3: [
                    `I acknowledge that the payment integrity terms apply to every payment made in connection with ${name} — including payments made on EAE and LearningFirst platforms.`,
                    'I acknowledge that using the rights and conveniences of payment platforms to obtain an unwarranted refund after consuming a content or service constitutes payment fraud.',
                    'I acknowledge that payment fraud is subject to sanctions at different levels, depending on the seriousness of the case.'
                ]
            }
        },
        pledgeMatches() {
            return this.normalize(this.pledgeInput) === this.normalize(this.pledgeTarget)
        },
        pledgeStatusText() {
            if (!this.pledgeInput) {
                return 'The pledge must be typed exactly as shown.'
            }
            if (this.pledgeMatches) {
                return '✓ The pledge matches. You may continue.'
            }
            return 'The pledge does not match yet. Keep typing (ignore case and leading/trailing spaces).'
        },
        canContinue() {
            if (this.skipValidation) {
                return true
            }
            if (this.step === 4) {
                return this.pledgeMatches
            }
            return this.checks[this.step].every(Boolean)
        }
    },
    methods: {
        normalize(value) {
            return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase()
        },
        toggleCheck(step, index) {
            this.$set(this.checks[step], index, !this.checks[step][index])
        },
        onProgressClick(target) {
            if (this.skipValidation || target <= this.maxVisited) {
                this.step = target
                if (target > this.maxVisited) {
                    this.maxVisited = target
                }
                this.scrollTop()
            }
        },
        goBack() {
            if (this.step === 1) {
                this.$emit('back')
                return
            }
            this.step -= 1
            this.scrollTop()
        },
        goNext() {
            if (!this.canContinue) {
                return
            }
            if (this.step === 4) {
                this.$emit('complete')
                return
            }
            this.step += 1
            if (this.step > this.maxVisited) {
                this.maxVisited = this.step
            }
            this.scrollTop()
        },
        scrollTop() {
            this.$nextTick(() => {
                const content = this.$el && this.$el.closest('.content')
                if (content) {
                    content.scrollTo({ top: 0, behavior: 'smooth' })
                } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                }
            })
        }
    }
}
</script>

<style scoped lang="scss">
.integrity-flow {
    --primary: #E00044;
    --secondary: #B91C3C;
    --deep: #991B1B;
    --accent: #F87171;
    --success: #27ae60;
    --warn: #f59e0b;
    --text-dark: #1f2937;
    --text-mid: #4b5563;
    --text-light: #6b7280;
    --border: #e5e7eb;
    color: var(--text-dark);
    line-height: 1.65;
}

.progress {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 8px 0 10px;
}

.prog-node {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
    background: #fff;
    border: 2px solid #d1d5db;
    color: #9ca3af;
    cursor: pointer;
    transition: all 0.3s ease;
}

.prog-node.active {
    border-color: var(--primary);
    color: var(--primary);
    box-shadow: 0 0 0 4px rgba(224, 0, 68, 0.13);
}

.prog-node.completed {
    background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
    border-color: var(--primary);
    color: #fff;
}

.prog-line {
    width: 56px;
    height: 2px;
    background: #e5e7eb;
    transition: background 0.3s ease;
}

.prog-line.completed {
    background: var(--primary);
}

.prog-label {
    text-align: center;
    font-size: 12px;
    color: var(--text-light);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 22px;
}

.step-tag {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--primary);
}

.step-head h2 {
    font-size: 28px;
    font-weight: 700;
    margin: 6px 0 14px;
    color: var(--text-dark);
}

.lead {
    font-size: 16px;
    color: var(--text-mid);
}

.lead strong {
    color: var(--primary);
    font-weight: 600;
}

.lead-gap {
    margin-top: 10px;
}

.block {
    margin-top: 22px;
    padding: 20px 22px;
    border: 1px solid var(--border);
    border-radius: 12px;
    background: #fff;
}

.block h3 {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 8px;
    color: var(--text-dark);
}

.block p {
    color: var(--text-mid);
    font-size: 15px;
}

.plain-list {
    margin: 10px 0 0 18px;
    color: var(--text-mid);
    font-size: 15px;
}

.plain-list li {
    margin-bottom: 6px;
}

.list-7 {
    margin: 10px 0 0;
    list-style: none;
    counter-reset: ch;
    padding: 0;
}

.list-7 li {
    counter-increment: ch;
    position: relative;
    padding: 9px 0 9px 40px;
    border-bottom: 1px dashed #f0f0f0;
    color: var(--text-mid);
    font-size: 14px;
}

.list-7 li:last-child {
    border-bottom: none;
}

.list-7 li::before {
    content: counter(ch);
    position: absolute;
    left: 0;
    top: 9px;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #FFF0F3;
    color: var(--primary);
    border: 1px solid #FBC5CF;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
}

.note {
    margin-top: 12px;
    padding: 10px 14px;
    background: #FFF8F0;
    border-left: 3px solid var(--warn);
    border-radius: 0 8px 8px 0;
    font-size: 13px;
    color: #7c4a03;
}

.section-hint {
    margin-top: 26px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-light);
}

.ack-list {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.ack {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    cursor: pointer;
    padding: 15px 16px;
    border: 1.5px solid var(--border);
    border-radius: 11px;
    background: #fff;
    transition: all 0.2s ease;
}

.ack:hover {
    border-color: var(--accent);
}

.ack.checked {
    border-color: var(--primary);
    background: #FFF6F7;
}

.custom-box {
    width: 22px;
    height: 22px;
    border: 2px solid #d1d5db;
    border-radius: 6px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
    transition: all 0.2s ease;
    background: #fff;
}

.ack.checked .custom-box {
    background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
    border-color: var(--primary);
}

.ack.checked .custom-box::after {
    content: '✓';
    color: #fff;
    font-size: 13px;
    font-weight: 700;
}

.ack-text {
    font-size: 14px;
    color: var(--text-mid);
    line-height: 1.5;
}

.ack.checked .ack-text {
    color: var(--text-dark);
}

.tiers {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.tier {
    padding: 16px 18px 16px 20px;
    border-radius: 0 12px 12px 0;
    border-left: 4px solid;
    background: #fafafa;
}

.tier-1 {
    border-color: var(--warn);
}

.tier-2 {
    border-color: var(--primary);
}

.tier-3 {
    border-color: var(--deep);
    background: #FFF5F6;
}

.tier-badge {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-light);
}

.tier-3 .tier-badge {
    color: var(--deep);
}

.tier-title {
    font-weight: 700;
    font-size: 15px;
    margin: 3px 0 4px;
    color: var(--text-dark);
}

.tier-desc {
    font-size: 14px;
    color: var(--text-mid);
}

.pledge-intro {
    margin-top: 24px;
    font-size: 14px;
    color: var(--text-mid);
    font-style: italic;
}

.pledge-box {
    margin-top: 12px;
    padding: 20px 22px;
    border-radius: 12px;
    background: #FDF3F5;
    border: 1px dashed var(--primary);
}

.pledge-label {
    font-style: italic;
    font-size: 16px;
    line-height: 1.75;
    color: #7f1d1d;
}

.pledge-guide {
    margin-top: 8px;
    font-size: 13px;
    color: #9a3b4f;
}

.pledge-input-wrap {
    margin-top: 16px;
}

.pledge-input {
    width: 100%;
    min-height: 108px;
    padding: 14px 16px;
    font-size: 15px;
    line-height: 1.7;
    border: 1.5px solid var(--border);
    border-radius: 10px;
    resize: vertical;
    outline: none;
    transition: all 0.2s ease;
    background: #fff;
    color: var(--text-dark);
    box-sizing: border-box;
    font-family: inherit;
}

.pledge-input:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(224, 0, 68, 0.1);
}

.pledge-input.match {
    border-color: var(--success);
    background: #F3FBF5;
}

.pledge-input.nomatch {
    border-color: var(--primary);
    background: #FFF5F6;
}

.pledge-status {
    margin-top: 8px;
    font-size: 13px;
    min-height: 20px;
    color: var(--text-light);
}

.pledge-status.ok {
    color: var(--success);
    font-weight: 600;
}

.pledge-status.err {
    color: var(--primary);
}

.nav-btns {
    display: flex;
    justify-content: space-between;
    gap: 14px;
    margin-top: 30px;
    margin-bottom: 40px;
}

.btn {
    padding: 12px 30px;
    border-radius: 999px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    letter-spacing: 0.02em;
}

.btn-primary {
    background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
    color: #fff;
    box-shadow: 0 4px 12px rgba(224, 0, 68, 0.28);
}

.btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(224, 0, 68, 0.34);
}

.btn-primary:disabled {
    opacity: 0.38;
    cursor: not-allowed;
    box-shadow: none;
}

.btn-ghost {
    background: transparent;
    color: var(--text-light);
    border: 1.5px solid var(--border);
}

.btn-ghost:hover {
    border-color: var(--text-light);
    color: var(--text-dark);
}

@media (max-width: 640px) {
    .prog-line {
        width: 30px;
    }

    .step-head h2 {
        font-size: 22px;
    }

    .nav-btns {
        flex-direction: column-reverse;
    }

    .btn {
        width: 100%;
        text-align: center;
    }
}
</style>
